import { useCallback, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { SiteModal } from '@/components/crm/SiteModal';
import { SiteCreationTypeModal } from '@/components/crm/SiteCreationTypeModal';
import { ProjectSelectorModal } from '@/components/crm/ProjectSelectorModal';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { useMapWithProvider } from '@/hooks/useMapWithProvider';
import { Project } from '@/types/crm';
// F9.12: welke sites energiemonitoring hebben komt in één vraag uit ems.locatie.site_id.
import { useSiteIdsMetEms, SITES_MET_EMS_SLEUTEL } from '@/features/ems/crm/hooks/useEms';
import { crmClient } from '@/features/ems/crm/data/crmClient';
// Een site met energiemonitoring wordt niet verwijderd: de dialoog weigert dan, en
// verwijderSite kijkt vlak vóór het schrijven zelf opnieuw.
import { emsKenmerkVoor, verwijderSite, VerwijderWeigering } from '@/features/sites/data/verwijderen';
// Na bewerken, aanmaken of verwijderen: ook de sitepagina (/crm/sites/:siteId) verversen.
import { naSiteWijziging, SITES_SLEUTEL } from '@/features/sites/data/sleutels';
import { LocatieLijst } from '@/features/sites/LocatieLijst';
import { filterNaarUrl, filterUitUrl, haalLocaties, type LijstFilter, type LijstLocatie } from '@/features/sites/data/lijst';
import { haalLive, type DashboardClient, type LiveLocatie } from '@/features/dashboard/data/dashboard';

/* /crm/sites. De lijst zelf staat in src/features/sites/LocatieLijst.tsx; hier de
   gegevens, de filter in de URL (zodat terug van een locatie de lijst herstelt), en de
   dialogen voor nieuw, bewerken en verwijderen. */

/** De vorm die SiteModal en de kaart kennen (public.sites). */
const alsRij = (l: LijstLocatie) => ({
  id: l.id,
  name: l.naam,
  address: l.adres ?? '',
  latitude: l.latitude as number,
  longitude: l.longitude as number,
  site_type: l.type ?? 'office',
  status: l.status ?? 'active',
  description: l.beschrijving,
  site_number: l.nummer ?? undefined,
  project_id: l.project_id ?? undefined,
  sharepoint_main_url: l.sharepoint,
  created_at: l.created_at,
  updated_at: l.updated_at ?? l.created_at,
});

export default function CRMSites() {
  const { userRole } = useAuth();
  const magBeheren = userRole === 'admin' || userRole === 'super_admin';
  const { MapComponent } = useMapWithProvider();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCreationTypeModalOpen, setIsCreationTypeModalOpen] = useState(false);
  const [isProjectSelectorOpen, setIsProjectSelectorOpen] = useState(false);
  const [selectedSite, setSelectedSite] = useState<ReturnType<typeof alsRij> | null>(null);
  const [prefilledSiteData, setPrefilledSiteData] = useState<any>(undefined);

  const lijst = useQuery({ queryKey: [...SITES_SLEUTEL, 'lijst'], queryFn: () => haalLocaties(crmClient) });
  const metEms = useSiteIdsMetEms(crmClient);
  const live = useQuery({
    queryKey: [...SITES_SLEUTEL, 'live'],
    queryFn: () => haalLive(crmClient as unknown as DashboardClient),
    staleTime: 60_000,
    /* In de pas met de ophaaldienst (elke 5 minuten). */
    refetchInterval: 5 * 60_000,
    refetchOnWindowFocus: false,
  });

  const filter = useMemo(() => filterUitUrl(searchParams), [searchParams]);
  const zetFilter = useCallback((f: LijstFilter) => setSearchParams(filterNaarUrl(f), { replace: true }), [setSearchParams]);
  const liveOpSite = useMemo(() => {
    const m = new Map<string, LiveLocatie>();
    for (const l of live.data ?? []) if (l.site_id && !m.has(l.site_id)) m.set(l.site_id, l);
    return m;
  }, [live.data]);
  const metEmsSet = useMemo(() => metEms.data ?? new Set<string>(), [metEms.data]);

  const ververs = () => {
    naSiteWijziging(queryClient);
  };

  const handleDelete = async (l: LijstLocatie) => {
    try {
      await verwijderSite(crmClient, l.id);
      toast({ title: 'Locatie verwijderd', description: `"${l.naam}" staat niet meer in Locaties.` });
      ververs();
    } catch (error: any) {
      if (error instanceof VerwijderWeigering) {
        toast({ title: 'Locatie niet verwijderd', description: error.message });
        /* de lijst dacht dat er geen EMS aan hing; laat het kenmerk opnieuw lezen */
        queryClient.invalidateQueries({ queryKey: SITES_MET_EMS_SLEUTEL });
        return;
      }
      toast({
        title: 'Fout bij verwijderen',
        description: `Er ging iets mis bij het verwijderen van de locatie. Details: ${error?.message ?? String(error)}`,
        variant: 'destructive',
      });
    }
  };

  const mapProjectTypeToSiteType = (projectType?: string | null): string => {
    const mapping: { [key: string]: string } = { nieuwbouw: 'construction', renovatie: 'office', uitbreiding: 'warehouse', onderhoud: 'office' };
    return mapping[projectType || ''] || 'office';
  };

  const handleCreateFromProject = (project: Project) => {
    setPrefilledSiteData({
      name: project.name,
      address: (project.organization as any)?.addresses?.[0]?.street || '',
      latitude: 52.3676,
      longitude: 4.9041,
      site_type: mapProjectTypeToSiteType(project.type),
      status: 'active',
      description: `Locatie aangemaakt vanuit project: ${project.name}`,
      project_id: project.id,
    });
    setIsModalOpen(true);
  };

  return (
    <>
      <LocatieLijst
        locaties={lijst.data ?? []}
        laden={lijst.isPending}
        fout={lijst.error as Error | null}
        metEms={metEmsSet}
        emsFout={metEms.isError ? (metEms.error instanceof Error ? metEms.error.message : String(metEms.error)) : null}
        kenmerkVoor={(id) => emsKenmerkVoor(metEms, id)}
        live={liveOpSite}
        filter={filter}
        onFilter={zetFilter}
        magBeheren={magBeheren}
        onNieuw={() => {
          setSelectedSite(null);
          setPrefilledSiteData(undefined);
          setIsCreationTypeModalOpen(true);
        }}
        onBewerk={(l) => {
          setSelectedSite(alsRij(l));
          setIsModalOpen(true);
        }}
        onVerwijder={handleDelete}
        kaart={(zichtbaar) => <MapComponent sites={zichtbaar.filter((l) => l.latitude !== null && l.longitude !== null).map(alsRij)} />}
      />

      <SiteCreationTypeModal
        isOpen={isCreationTypeModalOpen}
        onClose={() => setIsCreationTypeModalOpen(false)}
        onSelectManual={() => {
          setIsCreationTypeModalOpen(false);
          setIsModalOpen(true);
        }}
        onSelectFromProject={() => {
          setIsCreationTypeModalOpen(false);
          setIsProjectSelectorOpen(true);
        }}
      />

      <ProjectSelectorModal
        isOpen={isProjectSelectorOpen}
        onClose={() => setIsProjectSelectorOpen(false)}
        onSelectProject={handleCreateFromProject}
      />

      <SiteModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedSite(null);
          setPrefilledSiteData(undefined);
        }}
        site={selectedSite as any}
        onSave={() => {
          setIsModalOpen(false);
          setSelectedSite(null);
          setPrefilledSiteData(undefined);
          ververs();
        }}
        prefilledData={prefilledSiteData}
      />
    </>
  );
}
